/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import cloneDeep from "lodash/cloneDeep";
import ArgosFlexbox from "../../../../Containers/ArgosFlexbox";
import ArgosCheckbox from "../../../../Form/Checkbox/ArgosCheckbox";
import {TreeItem} from "@mui/x-tree-view/TreeItem";
import {SimpleTreeView} from "@mui/x-tree-view/SimpleTreeView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export const DialogTree = React.memo((props: any) => {
  const {sourceData, isMultiSelect} = props;
  const [expanded, setExpanded] = useState<string[]>([])
  const [checkedState, setCheckedState] = useState<any>({})

  useEffect(() => {
    if (props.selectedDatasets)
      setCheckedState(props.selectedDatasets)
    if (expanded.length > 0) return
    // go through the selected datasets and expand the tree to show them
    const _expanded: any = []
    if (!sourceData) return
    /**
     * Set expanded state
     */
    if (!props.fromSelectSource) {
      Object.keys(sourceData).forEach((studyId: string) => {
        const _selectedDatasetIds = Object.keys(props.selectedDatasets ?? {})
        if (_selectedDatasetIds.length === 0) return
        const studyTree = sourceData[studyId]
        // loop recursively through the tree and collect all parent nodeIds of the selected datasets. So search for a node with that datasetId, then get all its parents.
        // when we find a node with that datasetId, we add all its parents to the expanded array, and then we can exit.
        const _findNode = (tree: any, datasetIds: string[]) => {
          if (!tree) return false
          if (tree.datasetId && datasetIds.includes(tree.datasetId)) {
            _expanded.push(tree.nodeId)
            return true
          }
          if (tree?.children) {
            for (let i = 0; i < tree.children.length; i++) {
              const found = _findNode(tree.children[i], datasetIds)
              if (found) {
                _expanded.push(tree.nodeId)
                return true
              }
            }
          }
          return false
        }
        _findNode(studyTree, _selectedDatasetIds)
      })
    } else {
      // go to leaf nodes, and check the redlinesList for each. If it has a redlineList, check if any of the redlines are in the selectedDatasets. If so, expand the tree to that node
      // (add all its parents to the expanded array)
      const _findNode = (tree: any, datasetIds: string[]) => {
        if (!tree) return false
        if (tree.datasetId && datasetIds.includes(tree.datasetId)) {
          _expanded.push(tree.nodeId)
          return true
        }
        if (tree?.children) {
          for (let i = 0; i < tree.children.length; i++) {
            const found = _findNode(tree.children[i], datasetIds)
            if (found) {
              _expanded.push(tree.nodeId)
              // return true // TODO: Causes all but first child to not expand. With this commented, need to manual expand root but everything else works.
            }
          }
        }
        if (tree?.redlinesList) {
          for (let i = 0; i < tree.redlinesList.length; i++) {
            const redline = tree.redlinesList[i]
            if (datasetIds.includes(redline.datasetId)) {
              _expanded.push(tree.nodeId)
              return true
            }
          }
        }
        return false
      }
      _findNode(sourceData, Object.keys(props.selectedDatasets ?? {}))
      _expanded.push(sourceData?.nodeId)
    }
    setExpanded(_expanded)
  }, [props.selectedDatasets])

  const handleOnNodeChange = (event: any, nodes: any) => {
    event.stopPropagation()
    const _isChecked = event.target.checked
    // const nodeId = nodes?.nodeId
    const nodeId = !props.fromSelectSource ? nodes?.datasetId : nodes?.nodeId
    const _newCheckedMap = isMultiSelect ? cloneDeep(checkedState) : {}
    if (_isChecked) {
      if (!props.fromSelectSource) {
        _newCheckedMap[nodeId] = nodes?.dataset
      } else {
        // check current node
        _newCheckedMap[nodeId] = nodes

        // If it has children, check all children
        const _checkChildren = (tree: any) => {
          if (!tree) return
          if (tree?.children) {
            tree.children.forEach((child: any) => {
              _newCheckedMap[child.nodeId] = child
              _checkChildren(child)
            })
          }
        }
        _checkChildren(nodes)

        // check all parents
        checkParents(sourceData, nodes, true, _newCheckedMap);
      }
    } else {
      if (!props.fromSelectSource) {
        delete _newCheckedMap[nodeId]
      } else {
        // If it has children, uncheck all children
        const _uncheckChildren = (tree: any) => {
          if (!tree) return
          if (tree?.children) {
            tree.children.forEach((child: any) => {
              delete _newCheckedMap[child.nodeId]
              // If has redlinesList, loop and delete each
              if (child?.redlinesList) {
                child.redlinesList.forEach((redline: any) => {
                  delete _newCheckedMap[redline.datasetId]
                })
              }
              _uncheckChildren(child)
            })
          }
        }
        delete _newCheckedMap[nodeId]
        if (nodes?.redlinesList) {
          nodes.redlinesList.forEach((redline: any) => {
            delete _newCheckedMap[redline.datasetId]
          })
        }
        _uncheckChildren(nodes)
        checkParents(sourceData, nodes, false, _newCheckedMap)
      }
    }
    // const _newSelectedDataset = _isChecked ? nodes : null
    setCheckedState(_newCheckedMap)
    props?.setSelectedDataset(_newCheckedMap)
  }

  const getParent = (root: any, parentId: any) => {
    if (!root) return null;
    if (root.nodeId == parentId) {
      return root;
    }
    if (root?.children) {
      for (let i = 0; i < root.children.length; i++) {
        let found: any = getParent(root.children[i], parentId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  const checkParents = (root: any, node: any, check: boolean, _checkedMap: any) => {
    // If the node is null or the root, return
    if (!node || node == root) return;
    // Get the parent node id of the node
    let parentId = node.parentNodeId;
    let parent = getParent(root, parentId);
    // Get the children of the parent node
    let children = parent.children;
    // Initialize a variable to count the number of checked children
    let checkedCount = 0;
    let numChildren = 0;
    // write recursive function to count the number of checked children
    const countCheckedChildren = (tree: any) => {
      if (!tree) return;
      if (tree?.children) {
        tree.children.forEach((child: any) => {
          if (_checkedMap[child.nodeId]) {
            checkedCount++;
          }
          numChildren++;
          countCheckedChildren(child);
        })
      }
    }
    countCheckedChildren(parent);

    // If the check is true and all the children are checked, set the parent as checked
    if (check && checkedCount == numChildren) {
      _checkedMap[parent.nodeId] = parent;
    }
    // If the check is false and none of the children are checked, set the parent as unchecked
    else if (!check && checkedCount == 0) {
      // checkedMap[parent.nodeId] = "unchecked";
      delete _checkedMap[parent.nodeId]
      // If has redlinesList, loop and delete each
      if (parent?.redlinesList) {
        parent.redlinesList.forEach((redline: any) => {
          delete _checkedMap[redline.datasetId]
        })
      }
    }
    // Otherwise, set the parent as indeterminate
    else {
      _checkedMap[parent.nodeId] = {...parent, "indeterminate": true};
    }
    // Recursively call the function on the parent node
    checkParents(root, parent, check, _checkedMap);
  }

  const renderTree = (nodes: any) => {
    if (!nodes) return null
    const propToCheck = !props.fromSelectSource ? 'datasetId' : 'nodeId'
    const value = nodes[propToCheck]
    const isIndeterminate = checkedState?.[value]?.indeterminate
    const isChecked = checkedState?.hasOwnProperty(value)
    const isNodeIdInDisabledNodes = !isChecked ? props?.disabledNodes?.includes(nodes.nodeId) ?? false : false
    let treeItemLabel = nodes.name
    const nodeType = nodes.nodeType
    let labelContent = null
    if (!props.fromSelectSource && (nodeType !== 'dataset' || nodes?.children?.length)) { // types: study, folder, dataset
      labelContent = (
        <div style={{display: 'flex', height: 42, alignItems: 'center'}}>{treeItemLabel}</div>
      )
    } else {
      labelContent = (
        <ArgosFlexbox style={{alignItems: 'center',}}>
          <ArgosCheckbox
            tabIndex={-1}
            onChange={(event: any) => {
              handleOnNodeChange(event, nodes)
            }}
            onClick={(event: any) => {
              event.stopPropagation()
            }}
            checked={isChecked}
            indeterminate={isIndeterminate}
            disabled={isNodeIdInDisabledNodes}
          />
          <span>
            {treeItemLabel}
          </span>
        </ArgosFlexbox>
      )
    }

    return (
      <TreeItem
        key={nodes.nodeId + nodes.studyId}
        itemId={nodes.nodeId + nodes.studyId}
        label={labelContent}
      >
        {Array.isArray(nodes.children)
          ? nodes.children.map((node: any) => renderTree(node))
          : null}
      </TreeItem>
    );
  }

  if (!sourceData) return null
  let content = null
  if (!props.fromSelectSource) {
    content = (
      Object.keys(sourceData).map((studyId: string) => {
        const studyTree = sourceData[studyId]
        return (
          renderTree(studyTree)
        )
      })
    )
  } else {
    content = (
      renderTree(sourceData)
    )
  }
  return (
    <SimpleTreeView
      slots={{
        collapseIcon: ExpandMoreIcon,
        expandIcon: ChevronRightIcon
      }}
      // multiSelect={isMultiSelect}
      sx={{flexGrow: 1, overflowY: 'auto', padding: "20px"}}
      expandedItems={expanded}
      onExpandedItemsChange={(event: any, nodeIds: React.SetStateAction<string[]>) => {
        setExpanded(nodeIds)
      }}
    >
      {content}
    </SimpleTreeView>
  );
})